import { IAppContext } from 'App/AppContext';
import { allowedMimeTypesForUpload, globalAppName } from 'globalConstants';
import { TFunction } from 'i18next';
import mime from 'mime';
import ResourceLink from 'models/resourceLink';
import ResourceList from 'models/resourceList';
import { GraphScopeId } from 'services/Auth/authConfig';
import { graphUploadFileToFolder } from 'services/Graph/graphService';
import { IDriveItem } from 'services/Graph/SharepointInterfaces';
import { matchWildcard } from 'utils/string';

// A file pasted with Ctrl+V and its upload to a document library, shared by the dialog that adds library items
// (LinkCreatorDocumentLibrary) and the evidence of the Uitvraag uitkomsten (OutcomeEvidence), where a screenshot is
// pasted straight onto the question.

/** The files of a paste that may be uploaded; `invalid` when it held files, but none of a type that may be uploaded. */
export interface IClipboardFiles {
  files: File[];

  invalid: boolean;
}

/** The files of a paste whose type may be uploaded (allowedMimeTypesForUpload); text and other items are left out. */
export const getClipboardFiles = (items: DataTransferItemList | undefined): IClipboardFiles => {
  const files: File[] = [];
  let other: boolean = false;
  if (items === undefined) return { files: files, invalid: false };

  const allowed = Object.keys(allowedMimeTypesForUpload);
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.kind !== 'file') continue;

    if (allowed.some((type) => matchWildcard(item.type, type))) {
      const file = item.getAsFile();
      if (file) files.push(file);
    } else {
      other = true;
    }
  }

  return { files: files, invalid: files.length === 0 && other };
};

/** Two digits, for a time in a file name. */
const pad = (value: number): string => value.toString().padStart(2, '0');

/**
 * The name a pasted file gets, as the clipboard names every image the same ("image.png"): the prefix and the moment of
 * the paste, "Schermafbeelding 2026-10-23 143005.png", with the file's own extension.
 */
export const getPastedFileName = (prefix: string, file: File, now: Date = new Date()): string => {
  const dot = file.name.lastIndexOf('.');
  const extension = dot > 0 ? file.name.substring(dot + 1) : mime.getExtension(file.type) ?? 'png';
  const stamp =
    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ` +
    `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;

  return `${prefix.trim()} ${stamp}.${extension}`.trim();
};

/** A library item of the list for a file in its document library; not saved yet (its linkId is the new-link id). */
export const getLibraryLinkForDriveItem = (list: ResourceList, item: IDriveItem, base?: ResourceLink): ResourceLink => {
  const link = base ? base.clone() : new ResourceLink();
  link.listId = list.listId;
  link.list = list;
  link.linkName = item.name || 'Unknown';
  link.linkURL = item.webUrl || 'Unknown';
  link.driveItemId = item.id;

  return link;
};

/**
 * Uploads a file to a folder of the list's document library, the root without one, as `name`. Undefined when the list
 * has no drive or the file or the name is missing; a failed upload throws (graphUploadFileToFolder).
 */
export const uploadFileToLibrary = async (
  appContext: IAppContext,
  list: ResourceList,
  file: File,
  name: string,
  t: TFunction<string[]>,
  folder?: IDriveItem,
  onProgress?: (totalBytes: number, sendBytes: number) => void,
): Promise<IDriveItem | undefined> => {
  if (!list.spDriveId || !list.spDriveItemId) return undefined;
  if (!file || !name) return undefined;

  const graphInterface = await appContext.getGraphInterface(GraphScopeId.Library, list.altTenantId);

  return graphUploadFileToFolder(
    graphInterface.client,
    file,
    list.spDriveId,
    folder?.id || list.spDriveItemId,
    name,
    t('library:Picker.UploadDescription', { appName: globalAppName }),
    onProgress,
  );
};
